import { JSX, useContext, useEffect, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import useGetMsalAccessToken from '../features/users/services/useGetMsalAccessToken';
import { AppContext } from './App';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import { APProgressLoading } from '@ap/design-system';
import { setAuthToken, getUser } from '../helper/api';
import User from '../features/users/models/User';
import { UserTheme } from '../features/users/enums/UserTheme';
import { UserRegionalFormat } from '../features/users/enums/UserRegionalFormat';
import { UserRole } from '../features/users/enums/UserRole';

const containerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  flexDirection: 'column',
  gap: '16px',
  height: '100%',
  padding: '32px',
};

interface Props {
  readonly children: JSX.Element;
}

function AppUserProvider(props: Props) {
  const isAuthenticated = useIsAuthenticated();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user, setUser } = useContext(AppContext);
  const { getAccessToken } = useGetMsalAccessToken();

  useEffect(() => {
    const fetchAccessToken = async () => {
      if (isAuthenticated) {
        const entraAccessToken = await getAccessToken();

        if (!entraAccessToken) {
          console.error('Could not retrieve access token');
          return;
        }

        setAccessToken(entraAccessToken);
        setAuthToken(entraAccessToken);
      }
    };

    fetchAccessToken();
  }, [isAuthenticated, getAccessToken]);

  useEffect(() => {
    if (!accessToken) return;

    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const userData = await getUser();

        if (userData) {
          // Transform the API response to include access token and set defaults for missing fields
          const userWithToken: User = {
            ...userData,
            accessToken,
            // Use any cast to access unknown properties from Record<string, unknown>
            email: (userData as any).email || '',
            name: (userData as any).name || '',
            createdAt: (userData as any).createdAt ? new Date((userData as any).createdAt) : new Date(),
            updatedAt: (userData as any).updatedAt ? new Date((userData as any).updatedAt) : undefined,
            theme: ((userData as any).theme as UserTheme) || UserTheme.Light,
            regionalFormat: ((userData as any).regionalFormat as UserRegionalFormat) || UserRegionalFormat.ENGLISH_US,
            roles: ((userData as any).roles as UserRole[]) || [UserRole.SUPPORT],
            welcomeWizardCompleted: (userData as any).welcomeWizardCompleted || false,
            lastLoginAt: (userData as any).lastLoginAt ? new Date((userData as any).lastLoginAt) : undefined,
          };

          setUser(userWithToken);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [accessToken, setUser]);

  if (isLoading || !user) {
    return (
      <Container maxWidth='sm' sx={{ flex: 1 }}>
        <Box sx={containerStyle}>
          <APProgressLoading>Loading user data...</APProgressLoading>
        </Box>
      </Container>
    );
  }

  return <>{props.children}</>;
}

export default AppUserProvider;
